/**
 * LiveInterviewTranscriptPanel — Timestamp-Linked Feedback Annotations
 * feature: the LIVE, Teams-style transcript view shown during the call,
 * shared by RecruiterRoomPage (recruiter side) and LiveInterviewRoomPage
 * (candidate side). Same bubble/alignment conventions as
 * LiveInterviewChatPanel (own side right-aligned/purple, other side
 * left-aligned/white) — deliberately visually related (same visual
 * language, one workspace) but distinct enough at a glance: chat bubbles
 * are solid rounded-md; transcript lines are a flatter row with a role
 * label + timestamp, since these read continuously rather than as discrete
 * sent messages.
 *
 * Read-only — no send/compose here. Transcript lines arrive entirely via
 * `segments` (the parent's own onAppMessage-driven state, populated from
 * the signaling WebSocket's `transcript-segment` type — see
 * hooks/useLiveTranscription.ts) and via this hook's own `interimText`
 * (this browser's own in-progress recognition, never persisted/broadcast,
 * shown as ghost text at the bottom exactly like Teams' live caption).
 */
import React, { useEffect, useRef } from 'react'
import { PencilSquareIcon } from '@heroicons/react/24/outline'
import type { TranscriptionStatus } from '@/hooks/useLiveTranscription'

export interface LiveTranscriptSegment {
  id: string
  role: 'recruiter' | 'candidate'
  start_seconds: number
  end_seconds: number
  text: string
}

// Bounds how much LIVE scrollback either room page keeps in memory/renders
// during the call — an hours-long interview with continuous speech can
// accumulate thousands of lines, and neither the DOM nor a plain unbounded
// React array should grow forever for something that's meant to be read as
// "what's being said right now", not a full archive. The COMPLETE transcript
// is always safe in the database regardless (every segment is persisted the
// moment it's recognized) and is what the Summary/report page's Transcript
// section reads from in full, with its own lazy/paginated reveal — this cap
// only ever affects the LIVE, in-call view. Both room pages apply this same
// cap to the state array itself (not just what's rendered), so memory stays
// bounded too, not just the DOM.
export const MAX_LIVE_TRANSCRIPT_SEGMENTS = 300

interface LiveInterviewTranscriptPanelProps {
  ownRole: 'recruiter' | 'candidate'
  segments: LiveTranscriptSegment[]
  transcriptionStatus: TranscriptionStatus
  interimText?: string
  className?: string
  hideHeader?: boolean
  fillHeight?: boolean
}

function formatTimestamp(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

const STATUS_LABEL: Record<TranscriptionStatus, string> = {
  starting: 'Starting…',
  active: 'Live',
  reconnecting: 'Reconnecting…',
  unavailable: 'Transcript unavailable',
}

function StatusChip({ status }: { status: TranscriptionStatus }) {
  const color =
    status === 'active' ? 'bg-green-100 text-green-700'
      : status === 'reconnecting' ? 'bg-amber-100 text-amber-700'
        : status === 'unavailable' ? 'bg-gray-100 text-gray-500'
          : 'bg-gray-100 text-gray-500'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${color}`}>
      {status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />}
      {STATUS_LABEL[status]}
    </span>
  )
}

export default function LiveInterviewTranscriptPanel({
  ownRole, segments, transcriptionStatus, interimText, className, hideHeader, fillHeight,
}: LiveInterviewTranscriptPanelProps) {
  const listRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to the newest line — same UX as the chat panel.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight
  }, [segments.length, interimText])

  return (
    <div className={`flex flex-col h-full min-h-0 ${className || ''}`}>
      {!hideHeader && (
        <div className="flex-shrink-0 flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
            <PencilSquareIcon className="w-3.5 h-3.5" /> Live Transcript
          </p>
          <StatusChip status={transcriptionStatus} />
        </div>
      )}
      <div
        ref={listRef}
        className={`flex-1 overflow-y-auto space-y-1.5 rounded-md p-2.5 bg-gray-50 border border-gray-200 ${fillHeight ? 'min-h-0' : 'min-h-[140px] max-h-64'}`}
      >
        {segments.length === 0 && !interimText ? (
          <p className="text-xs text-center py-4 text-gray-400">
            {transcriptionStatus === 'unavailable' ? 'Live transcript unavailable.' : 'Waiting for someone to speak…'}
          </p>
        ) : (
          <>
            {segments.length >= MAX_LIVE_TRANSCRIPT_SEGMENTS && (
              <p className="text-[10px] text-center text-gray-400 pb-1">
                Showing the most recent {MAX_LIVE_TRANSCRIPT_SEGMENTS} lines — the full transcript is always saved and available in the report after the interview.
              </p>
            )}
            {segments.map((s) => {
              const isOwn = s.role === ownRole
              return (
                <div key={s.id} className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-md px-2.5 py-1.5 text-xs ${
                      isOwn ? 'bg-purple-600 text-white' : 'bg-white border border-gray-200 text-gray-800'
                    }`}
                  >
                    {s.text}
                  </div>
                  <span className="text-[10px] mt-0.5 text-gray-400">
                    {s.role === 'recruiter' ? 'Recruiter' : 'Candidate'} · {formatTimestamp(s.start_seconds)}
                  </span>
                </div>
              )
            })}
          </>
        )}
        {interimText && (
          <div className={`flex flex-col ${ownRole === 'recruiter' ? 'items-end' : 'items-start'}`}>
            <div className="max-w-[85%] rounded-md px-2.5 py-1.5 text-xs italic text-gray-400 bg-white/60 border border-dashed border-gray-300">
              {interimText}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
